import {
    BotProviderType,
    ChatBotFeedbackRating,
    ChatBotHistory,
    ChatBotHistoryUserSettingSearch,
    ChatBotHistoryWithUserSettings,
    getApiChatBotHistoryGetAfter,
    getApiChatBotHistoryGetBefore,
    getGetApiBotProviderGetQueryKey,
    getGetApiChatBotHistoryGetContextQueryKey,
    useGetApiBotProviderGet,
    useGetApiChatBotHistoryGetContext,
    useGetApiWorkspaceGet,
    usePostApiChatBotHistorySearchFeedbackWithUserSettings
} from "@/API";
import { AppBreadcrumbs } from "@/Components/AppBreadcrumbs";
import { MarkdownMessage } from "@/Components/MarkdownMessage";
import { DateToTimestamp, TimestampToDate } from "@/Helpers/DateHelper";
import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Card,
    Center,
    Divider,
    Group,
    LoadingOverlay,
    Modal,
    Pagination,
    Paper,
    ScrollArea,
    SegmentedControl,
    Stack,
    Text,
    TextInput,
    ThemeIcon,
    Tooltip
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconArrowDown, IconArrowUp, IconExternalLink, IconEye, IconMessage, IconThumbDown, IconThumbUp } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { DataTable } from "mantine-datatable";
import { useEffect, useRef, useState } from "react";

export function ChatBotFeedbackPage(props: { workspaceId: string; botProviderId: string }) {
    const navigate = useNavigate();
    const workspace = useGetApiWorkspaceGet({ id: props.workspaceId });
    const botProvider = useGetApiBotProviderGet(
        { workspaceId: props.workspaceId, id: props.botProviderId },
        {
            query: {
                queryKey: getGetApiBotProviderGetQueryKey({ workspaceId: props.workspaceId, id: props.botProviderId }),
                enabled: !!props.botProviderId
            }
        }
    );

    const [activePage, setPage] = useState(1);
    const PAGE_SIZE = 20;
    const search_Mutation = usePostApiChatBotHistorySearchFeedbackWithUserSettings();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<ChatBotHistoryWithUserSettings[]>([]);
    const [total, setTotal] = useState(0);

    const [ratingFilter, setRatingFilter] = useState<"All" | "Good" | "Bad">("All");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [userName, setUserName] = useState<string>("");

    const [selectedContextRow, setSelectedContextRow] = useState<ChatBotHistoryWithUserSettings | null>(null);
    const [modalHistoryList, setModalHistoryList] = useState<ChatBotHistory[]>([]);
    const [loadingMoreBefore, setLoadingMoreBefore] = useState(false);
    const [loadingMoreAfter, setLoadingMoreAfter] = useState(false);
    const [hasNoMoreBefore, setHasNoMoreBefore] = useState(false);
    const [hasNoMoreAfter, setHasNoMoreAfter] = useState(false);

    const modalViewportRef = useRef<HTMLDivElement>(null);
    const prevModalScrollHeightRef = useRef<number | null>(null);

    const contextQueryParams = {
        historyId: selectedContextRow?.id ?? "",
        beforeCount: 5,
        afterCount: 5
    };

    const contextQuery = useGetApiChatBotHistoryGetContext(
        contextQueryParams,
        {
            query: {
                queryKey: getGetApiChatBotHistoryGetContextQueryKey(contextQueryParams),
                enabled: !!selectedContextRow?.id
            }
        }
    );

    useEffect(() => {
        if (contextQuery.data?.data) {
            setModalHistoryList(contextQuery.data.data);
            setHasNoMoreBefore(false);
            setHasNoMoreAfter(false);
        }
    }, [contextQuery.data]);

    useEffect(() => {
        if (prevModalScrollHeightRef.current !== null && modalViewportRef.current) {
            const heightDiff = modalViewportRef.current.scrollHeight - prevModalScrollHeightRef.current;
            modalViewportRef.current.scrollTop += heightDiff;
            prevModalScrollHeightRef.current = null;
        }
    }, [modalHistoryList]);

    const handleLoadBefore = async () => {
        if (!selectedContextRow || modalHistoryList.length === 0) return;
        const earliestTs = modalHistoryList[0]?.createdTimestamp;
        if (!earliestTs) return;

        if (modalViewportRef.current) {
            prevModalScrollHeightRef.current = modalViewportRef.current.scrollHeight;
        }

        setLoadingMoreBefore(true);
        try {
            const res = await getApiChatBotHistoryGetBefore({
                botProviderId: props.botProviderId,
                userId: selectedContextRow.userId ?? "",
                timestamp: earliestTs,
                count: 5
            });
            const newItems = res.data ?? [];
            if (newItems.length > 0) {
                setModalHistoryList((prev) => [...newItems, ...prev]);
            }
            if (newItems.length < 5) {
                setHasNoMoreBefore(true);
            }
        } finally {
            setLoadingMoreBefore(false);
        }
    };

    const handleLoadAfter = async () => {
        if (!selectedContextRow || modalHistoryList.length === 0) return;
        const latestTs = modalHistoryList[modalHistoryList.length - 1]?.createdTimestamp;
        if (!latestTs) return;

        setLoadingMoreAfter(true);
        try {
            const res = await getApiChatBotHistoryGetAfter({
                botProviderId: props.botProviderId,
                userId: selectedContextRow.userId ?? "",
                timestamp: latestTs,
                count: 5
            });
            const newItems = res.data ?? [];
            if (newItems.length > 0) {
                setModalHistoryList((prev) => [...prev, ...newItems]);
            }
            if (newItems.length < 5) {
                setHasNoMoreAfter(true);
            }
        } finally {
            setLoadingMoreAfter(false);
        }
    };

    useEffect(() => {
        ExecuteSearch();
    }, [activePage, ratingFilter, startDate, endDate]);

    function ExecuteSearch() {
        setLoading(true);
        const feedbackRatingParam =
            ratingFilter === "Good"
                ? ChatBotFeedbackRating.Good
                : ratingFilter === "Bad"
                    ? ChatBotFeedbackRating.Bad
                    : undefined;

        let startTimestamp: number | undefined = undefined;
        if (startDate) {
            const startOfDay = new Date(startDate);
            if (!isNaN(startOfDay.getTime())) {
                startTimestamp = DateToTimestamp(startOfDay);
            }
        }

        let endTimestamp: number | undefined = undefined;
        if (endDate) {
            const endOfDay = new Date(endDate);
            if (!isNaN(endOfDay.getTime())) {
                endOfDay.setHours(23, 59, 59, 999);
                endTimestamp = DateToTimestamp(endOfDay);
            }
        }

        const searchPayload: ChatBotHistoryUserSettingSearch = {
            botProviderId: props.botProviderId,
            userName: userName.trim() !== "" ? userName : undefined,
            hasFeedback: true,
            feedbackRating: feedbackRatingParam,
            startTimestamp: startTimestamp,
            endTimestamp: endTimestamp
        };

        search_Mutation
            .mutateAsync({
                data: searchPayload,
                params: {
                    skip: (activePage - 1) * PAGE_SIZE,
                    limit: PAGE_SIZE
                }
            })
            .then((x) => {
                setData(x.data.results ?? []);
                setTotal(x.data.total ?? 0);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const goodCountInPage = data.filter((x) => x.feedback?.rating === ChatBotFeedbackRating.Good).length;
    const badCountInPage = data.filter((x) => x.feedback?.rating === ChatBotFeedbackRating.Bad).length;

    return (
        <Box pos="relative">
            <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
            <AppBreadcrumbs
                items={[
                    {
                        label: "工作空間列表",
                        linkProps: { to: "/workspace" }
                    },
                    {
                        label: workspace.data?.data.name ?? "",
                        linkProps: { to: "/workspace/$workspaceId", params: { workspaceId: props.workspaceId } }
                    },
                    {
                        label: "機器人串接",
                        linkProps: { to: "/workspace/$workspaceId/botProvider", params: { workspaceId: props.workspaceId } }
                    },
                    {
                        label: (botProvider.data?.data.name ?? "") + " 使用者回饋",
                        active: true
                    }
                ]}
            />

            <Group mt="md" mb="md" grow wrap="wrap">
                <Paper p="sm" withBorder radius="md">
                    <Text size="xs" c="dimmed" fw={500}>
                        總回饋筆數
                    </Text>
                    <Text size="xl" fw={700}>
                        {total}
                    </Text>
                </Paper>
                <Paper p="sm" withBorder radius="md">
                    <Group justify="space-between" align="center">
                        <div>
                            <Text size="xs" c="dimmed" fw={500}>
                                本頁正向回饋 👍
                            </Text>
                            <Text size="xl" fw={700} c="green">
                                {goodCountInPage}
                            </Text>
                        </div>
                        <ThemeIcon color="green" variant="light" size="lg" radius="md">
                            <IconThumbUp size={20} />
                        </ThemeIcon>
                    </Group>
                </Paper>
                <Paper p="sm" withBorder radius="md">
                    <Group justify="space-between" align="center">
                        <div>
                            <Text size="xs" c="dimmed" fw={500}>
                                本頁負向回饋 👎
                            </Text>
                            <Text size="xl" fw={700} c="red">
                                {badCountInPage}
                            </Text>
                        </div>
                        <ThemeIcon color="red" variant="light" size="lg" radius="md">
                            <IconThumbDown size={20} />
                        </ThemeIcon>
                    </Group>
                </Paper>
            </Group>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    if (activePage !== 1) {
                        setPage(1);
                    } else {
                        ExecuteSearch();
                    }
                }}
            >
                <Paper p="sm" withBorder mb="md">
                    <Stack gap="xs">
                        <Group align="center">
                            <Text size="sm" fw={500}>
                                回饋類型：
                            </Text>
                            <SegmentedControl
                                value={ratingFilter}
                                onChange={(val: string) => {
                                    setRatingFilter(val as "All" | "Good" | "Bad");
                                    setPage(1);
                                }}
                                data={[
                                    { label: "全部", value: "All" },
                                    { label: "👍 正向回饋", value: "Good" },
                                    { label: "👎 負向回饋", value: "Bad" }
                                ]}
                            />
                        </Group>
                        <Group wrap="wrap" align="flex-end">
                            <Box w={220}>
                                <Text size="xs" mb={3}>
                                    使用者名稱
                                </Text>
                                <TextInput
                                    placeholder="搜尋使用者名稱"
                                    value={userName}
                                    onChange={(e) => setUserName(e.target.value)}
                                />
                            </Box>
                            <Group align="flex-end" gap="xs">
                                <Box w={150}>
                                    <Text size="xs" mb={3}>
                                        開始日期
                                    </Text>
                                    <DatePickerInput
                                        valueFormat="YYYY/MM/DD"
                                        placeholder="選擇開始日期"
                                        value={startDate}
                                        onChange={(val) => {
                                            const d = typeof val === "string" ? (val ? new Date(val) : null) : val;
                                            setStartDate(d);
                                            setPage(1);
                                        }}
                                        clearable
                                        maxDate={endDate ?? undefined}
                                    />
                                </Box>
                                <Text size="sm" pb={8}>
                                    ～
                                </Text>
                                <Box w={150}>
                                    <Text size="xs" mb={3}>
                                        結束日期
                                    </Text>
                                    <DatePickerInput
                                        valueFormat="YYYY/MM/DD"
                                        placeholder="選擇結束日期"
                                        value={endDate}
                                        onChange={(val) => {
                                            const d = typeof val === "string" ? (val ? new Date(val) : null) : val;
                                            setEndDate(d);
                                            setPage(1);
                                        }}
                                        clearable
                                        minDate={startDate ?? undefined}
                                    />
                                </Box>
                            </Group>
                            <Button type="submit" loading={loading}>
                                搜尋
                            </Button>
                        </Group>
                    </Stack>
                </Paper>
            </form>

            <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">
                    共找到 {total} 筆回饋紀錄
                </Text>
                <Pagination value={activePage} onChange={setPage} total={Math.ceil(total / PAGE_SIZE) || 1} />
            </Group>

            <DataTable
                withTableBorder
                minHeight={data.length > 0 ? 0 : 150}
                noRecordsText="沒有回饋資料"
                records={data}
                columns={[
                    {
                        accessor: "userSettings.name",
                        title: "使用者",
                        render: (row) => (
                            <Stack gap={2}>
                                <Text size="sm" fw={600}>
                                    {row.userSettings?.name || "匿名使用者"}
                                </Text>
                                <Text size="xs" c="dimmed">
                                    {row.userId}
                                </Text>
                            </Stack>
                        )
                    },
                    {
                        accessor: "feedback.rating",
                        title: "回饋評等",
                        render: (row) => {
                            const isGood = row.feedback?.rating === ChatBotFeedbackRating.Good;
                            const isBad = row.feedback?.rating === ChatBotFeedbackRating.Bad;
                            return (
                                <Badge
                                    color={isGood ? "green" : isBad ? "red" : "gray"}
                                    variant="light"
                                    leftSection={
                                        isGood ? (
                                            <IconThumbUp size={12} />
                                        ) : isBad ? (
                                            <IconThumbDown size={12} />
                                        ) : (
                                            <IconMessage size={12} />
                                        )
                                    }
                                >
                                    {isGood ? "正向回饋" : isBad ? "負向回饋" : "一般"}
                                </Badge>
                            );
                        }
                    },
                    {
                        accessor: "feedback.comment",
                        title: "評語 / 建議",
                        render: (row) => (
                            <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {row.feedback?.comment || <Text component="span" c="dimmed" fs="italic">無文字評語</Text>}
                            </Text>
                        )
                    },
                    {
                        accessor: "records",
                        title: "對話預覽",
                        render: (row) => {
                            const userMsg = row.records?.find((r) => r.role === "User")?.message;
                            const assistantMsg = row.records?.find((r) => r.role === "Assistant")?.message;
                            return (
                                <Stack gap={4} maw={350}>
                                    {userMsg && (
                                        <Text size="xs" c="dimmed" lineClamp={2}>
                                            <Text component="span" fw={600}>問：</Text>{userMsg}
                                        </Text>
                                    )}
                                    {assistantMsg && (
                                        <Text size="xs" lineClamp={3}>
                                            <Text component="span" fw={600} c="blue">答：</Text>{assistantMsg}
                                        </Text>
                                    )}
                                </Stack>
                            );
                        }
                    },
                    {
                        accessor: "feedback.timestamp",
                        title: "回饋時間",
                        render: (row) => {
                            const ts = row.feedback?.timestamp || row.createdTimestamp;
                            return ts ? TimestampToDate(ts).toLocaleString() : "-";
                        }
                    },
                    {
                        accessor: "actions",
                        title: "操作",
                        render: (row) => (
                            <Group gap={6} wrap="nowrap">
                                <Tooltip label="檢視觸發回饋時的前後對話歷程">
                                    <Button
                                        size="xs"
                                        variant="light"
                                        color="blue"
                                        leftSection={<IconEye size={14} />}
                                        onClick={() => setSelectedContextRow(row)}
                                    >
                                        前後對話
                                    </Button>
                                </Tooltip>
                                <Tooltip label="跳轉至該使用者的完整聊天紀錄">
                                    <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                        onClick={() => {
                                            navigate({
                                                to: "/workspace/$workspaceId/botProvider/$botProviderId/user/$userId",
                                                params: {
                                                    workspaceId: props.workspaceId,
                                                    botProviderId: props.botProviderId,
                                                    userId: row.userId ?? ""
                                                }
                                            });
                                        }}
                                    >
                                        <IconExternalLink size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        )
                    }
                ]}
            />

            <Modal
                opened={!!selectedContextRow}
                onClose={() => {
                    setSelectedContextRow(null);
                    setModalHistoryList([]);
                }}
                title={
                    <Group gap="xs">
                        <IconEye size={20} />
                        <Text fw={600} size="md">
                            前後對話脈絡切片 ({selectedContextRow?.userSettings?.name || selectedContextRow?.userId})
                        </Text>
                    </Group>
                }
                size="lg"
            >
                <Box pos="relative" mih={200}>
                    <LoadingOverlay visible={contextQuery.isFetching} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
                    <ScrollArea.Autosize viewportRef={modalViewportRef} mah={550} type="auto">
                        <Stack gap="md" p="xs">
                            {modalHistoryList.length > 0 && (
                                <>
                                    {!hasNoMoreBefore ? (
                                        <Center>
                                            <Button
                                                size="xs"
                                                variant="light"
                                                leftSection={<IconArrowUp size={14} />}
                                                loading={loadingMoreBefore}
                                                onClick={handleLoadBefore}
                                            >
                                                載入更早對話 (往前 5 筆)
                                            </Button>
                                        </Center>
                                    ) : (
                                        <Center>
                                            <Text size="xs" c="dimmed">
                                                已到達最早對話紀錄
                                            </Text>
                                        </Center>
                                    )}

                                    {modalHistoryList.map((item, idx) => {
                                        const isTarget = item.id === selectedContextRow?.id;
                                        const isGood = item.feedback?.rating === ChatBotFeedbackRating.Good;
                                        const isBad = item.feedback?.rating === ChatBotFeedbackRating.Bad;

                                        return (
                                            <Paper
                                                key={item.id || idx}
                                                p="md"
                                                radius="md"
                                                withBorder
                                                style={
                                                    isTarget
                                                        ? {
                                                            border: "2px solid var(--mantine-color-blue-filled)",
                                                            backgroundColor: "var(--mantine-color-blue-light)"
                                                        }
                                                        : {
                                                            backgroundColor: "var(--mantine-color-body)"
                                                        }
                                                }
                                            >
                                                <Group justify="space-between" mb="xs">
                                                    <Group gap="xs">
                                                        {isTarget && (
                                                            <Badge color="blue" variant="filled">
                                                                🎯 當前檢視之回饋對話
                                                            </Badge>
                                                        )}
                                                        <Text size="xs" c="dimmed">
                                                            {item.createdTimestamp ? TimestampToDate(item.createdTimestamp).toLocaleString() : ""}
                                                        </Text>
                                                    </Group>
                                                    {item.feedback && (
                                                        <Badge
                                                            color={isGood ? "green" : isBad ? "red" : "gray"}
                                                            variant="light"
                                                            leftSection={isGood ? <IconThumbUp size={12} /> : <IconThumbDown size={12} />}
                                                        >
                                                            {isGood ? "正向回饋" : "負向回饋"}
                                                        </Badge>
                                                    )}
                                                </Group>

                                                {item.feedback?.comment && (
                                                    <Paper p="xs" mb="xs" bg="var(--mantine-color-body)" withBorder radius="sm">
                                                        <Text size="xs" fw={600} c={isGood ? "green" : isBad ? "red" : "blue"}>
                                                            使用者評語：{item.feedback.comment}
                                                        </Text>
                                                    </Paper>
                                                )}

                                                <Stack gap="xs">
                                                    {item.records?.map((record, rIdx) => {
                                                        const isUser = record.role === "User";
                                                        return (
                                                            <Box
                                                                key={rIdx}
                                                                p="xs"
                                                                bg={isUser ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-default-hover)"}
                                                                c={isUser ? "white" : "var(--mantine-color-text)"}
                                                                style={{ borderRadius: 8, wordBreak: "break-word" }}
                                                            >
                                                                <Text size="xs" fw={600} mb={2} c={isUser ? "white" : "dimmed"}>
                                                                    {isUser ? "使用者" : "機器人"}
                                                                </Text>
                                                                {isUser ? (
                                                                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                                                                        {record.message}
                                                                    </Text>
                                                                ) : (
                                                                    <MarkdownMessage>{record.message || ""}</MarkdownMessage>
                                                                )}
                                                            </Box>
                                                        );
                                                    })}
                                                </Stack>
                                            </Paper>
                                        );
                                    })}

                                    {!hasNoMoreAfter ? (
                                        <Center>
                                            <Button
                                                size="xs"
                                                variant="light"
                                                leftSection={<IconArrowDown size={14} />}
                                                loading={loadingMoreAfter}
                                                onClick={handleLoadAfter}
                                            >
                                                載入更晚對話 (往後 5 筆)
                                            </Button>
                                        </Center>
                                    ) : (
                                        <Center>
                                            <Text size="xs" c="dimmed">
                                                已到達最新對話紀錄
                                            </Text>
                                        </Center>
                                    )}
                                </>
                            )}

                            {modalHistoryList.length === 0 && !contextQuery.isFetching && (
                                <Text size="sm" c="dimmed" ta="center" py="xl">
                                    無上下文對話紀錄
                                </Text>
                            )}
                        </Stack>
                    </ScrollArea.Autosize>
                </Box>
            </Modal>
        </Box>
    );
}
